import { useCallback, useEffect, useState } from 'react';
import type { VerificationType, VerifiedCertificateItem } from '@mvcp/contracts';
import { listVerifiedCertificates, revokeCertificate } from '../api';
import { MAX_REASON_LEN } from '../lib/field-limits';
import { ConfirmModal } from './ConfirmModal';
import { Skeleton } from './Skeleton';
import { StatusPill } from './statusPill';

/**
 * Provenance ("assurance") marker labels (Story 5.2/5.4/5.5), orthogonal to any status.
 * Re-inlined here because the web consumes `@mvcp/contracts` as TYPES ONLY (a
 * runtime-value import breaks `vite build`, Story 1.6). Honest wording: `self_declared`
 * is NEVER presented as equivalent to a third-party certification.
 */
const PROVENANCE_LABEL: Record<VerificationType, string> = {
  third_party: 'Third-party verified',
  self_declared: 'Self-declared',
};

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function shortDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: VerifiedCertificateItem[] }
  | { kind: 'error'; message: string };

/**
 * System-Administrator verified-certificates surface (Story 5.5). Lists the trusted
 * backbone Certificates (status='verified') that Story 5.4 platform verification
 * produced; per row the admin can Revoke one — a destructive action that moves the ONE
 * shared record out of the trusted state IN PLACE, so every Company using the Vendor
 * reflects the downgrade by shared-read (their scores drop on next read, no per-Company
 * action). Mirrors {@link VerificationQueueView}'s loading/error/Retry contract. NO
 * customer identity is rendered (the payload carries none); the Vendor's OWN name IS
 * shown (the admin curates the directory).
 */
export function VerifiedCertificatesView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  // The cert whose revoke-with-reason modal is open (null when closed).
  const [revoking, setRevoking] = useState<VerifiedCertificateItem | null>(null);
  const [reason, setReason] = useState('');
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const items = await listVerifiedCertificates();
      setState({ kind: 'ready', items });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openRevoke(item: VerifiedCertificateItem): void {
    setRevoking(item);
    setReason('');
    setRevokeError(null);
  }

  async function onConfirmRevoke(): Promise<void> {
    if (!revoking) return;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setRevokeError('A reason is required — it is recorded in the audit log.');
      return;
    }
    setRevokeError(null);
    setRevokeBusy(true);
    try {
      await revokeCertificate(revoking.id, trimmed);
      setRevoking(null);
      await load();
    } catch (err: unknown) {
      setRevokeError(err instanceof Error ? err.message : "Couldn't revoke. Try again.");
    } finally {
      setRevokeBusy(false);
    }
  }

  return (
    <>
      <div>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Verified certificates
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Trusted certificates every company that uses the vendor counts as compliant. Revoking one
          moves it out of trusted status for every company; the action is logged.
        </p>
      </div>

      {state.kind === 'loading' && (
        <section className="card stack" aria-busy="true" aria-label="Loading verified certificates">
          <Skeleton height={20} width="40%" />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </section>
      )}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Couldn’t load verified certificates
          </h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void load()}
            style={{ alignSelf: 'flex-start' }}
          >
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && (
        <section className="card stack" aria-labelledby="verified-heading">
          <h2 id="verified-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            Trusted certificates
          </h2>

          {state.items.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>No certificates are verified yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {state.items.map((item) => (
                <li
                  key={item.id}
                  className="stack"
                  data-verified-item={item.id}
                  style={{ gap: 6, borderTop: '1px solid var(--border)', paddingTop: 10 }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }} data-numeric>
                      {item.framework.code}
                    </span>
                    <span style={{ fontWeight: 600 }}>{item.framework.name}</span>
                    <span
                      className="status-pill"
                      data-tone="neutral"
                      data-provenance={item.framework.verificationType}
                      aria-label={`Provenance: ${PROVENANCE_LABEL[item.framework.verificationType]}`}
                    >
                      {PROVENANCE_LABEL[item.framework.verificationType]}
                    </span>
                    <StatusPill status={item.status} />
                  </div>
                  <div className="muted" style={{ fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span>
                      Vendor: <span style={{ fontWeight: 600 }}>{item.vendorName}</span>
                    </span>
                    <span>
                      Subject: {item.subjectType === 'device' ? (item.deviceLabel ?? 'Device') : 'Company'}
                    </span>
                    <span>Authority: {item.approvingAuthority ?? '—'}</span>
                    <span>Expires: {shortDate(item.expiresAt)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="icon-button"
                      data-tone="bad"
                      onClick={() => openRevoke(item)}
                      style={{ marginLeft: 'auto' }}
                    >
                      Revoke
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {revoking && (
        <ConfirmModal
          title="Revoke certificate"
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                This moves the certificate out of trusted status for every company that uses this
                vendor; their compliance scores will drop on next read. This action is logged.
              </p>
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Reason</span>
                <textarea
                  aria-label="Revocation reason"
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (revokeError) setRevokeError(null);
                  }}
                  rows={3}
                  maxLength={MAX_REASON_LEN}
                  style={{
                    padding: '8px 10px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--rounded-md)',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    resize: 'vertical',
                  }}
                />
              </label>
            </div>
          }
          confirmLabel="Revoke"
          busy={revokeBusy}
          error={revokeError}
          onConfirm={() => void onConfirmRevoke()}
          onCancel={() => setRevoking(null)}
        />
      )}
    </>
  );
}
